import React, { useState } from "react";
import { Input, Select } from "antd";
import { PhoneOutlined } from "@ant-design/icons";
import { CountryCodes } from "../../Constants";
import { toEnglishDigits } from "../../utils/digits";

// Phone input with a country-code dropdown. Value in/out is one plain string ("+966501234567",
// or "" while the number is empty), so it drops into Form.Item and a "required" rule still works.
// Saudi Arabia is the default code; existing values are split on their longest matching code.
// The guest may type the number with or without the leading 0 (0501234567 or 501234567): the zero
// stays visible while typing and is dropped from the saved value.

const DEFAULT_CODE = "+966";
const CODES_LONGEST_FIRST = [...CountryCodes].sort((a, b) => b.code.length - a.code.length);

const parsePhone = (raw, defaultCode) => {
  const cleaned = toEnglishDigits(String(raw || "")).replace(/[^\d+]/g, "");
  if (!cleaned) return { code: defaultCode, number: "" };
  const international = cleaned.startsWith("00") ? `+${cleaned.slice(2)}` : cleaned;
  if (international.startsWith("+")) {
    const match = CODES_LONGEST_FIRST.find((c) => international.startsWith(c.code));
    if (match) return { code: match.code, number: international.slice(match.code.length) };
    return { code: defaultCode, number: international.replace(/\D/g, "") };
  }
  // Local format such as 0501234567: drop the trunk zero and use the default code.
  return { code: defaultCode, number: international.replace(/^0+/, "") };
};

const PhoneField = ({ value, onChange, defaultCode = DEFAULT_CODE, placeholder = "50 123 4567", disabled, size }) => {
  const parsed = parsePhone(value, defaultCode);
  const number = parsed.number;
  // An empty number is saved as "", which carries no code: remember the one picked meanwhile.
  const [pickedCode, setPickedCode] = useState(null);
  const code = number ? parsed.code : pickedCode ?? parsed.code;
  // What the person actually typed, leading zero included. The saved value never has the zero.
  const [typed, setTyped] = useState("");

  const emit = (nextCode, nextNumber) => {
    const digits = toEnglishDigits(nextNumber).replace(/\D/g, "").replace(/^0+/, "");
    setPickedCode(nextCode);
    onChange?.(digits ? `${nextCode}${digits}` : "");
  };

  // Show the typed text while it still matches the saved number (only the zero differs); otherwise
  // the value changed from outside (edit form loaded, code changed) and the saved number is shown.
  const shown = typed && typed.replace(/^0+/, "") === number ? typed : number;

  return (
    <Input
      dir="ltr"
      size={size}
      type="tel"
      inputMode="tel"
      disabled={disabled}
      value={shown}
      placeholder={placeholder}
      prefix={<PhoneOutlined />}
      onChange={(e) => {
        const raw = toEnglishDigits(e.target.value).replace(/\D/g, "");
        setTyped(raw);
        emit(code, raw);
      }}
      addonBefore={
        <Select
          showSearch
          disabled={disabled}
          size={size}
          value={code}
          onChange={(next) => emit(next, number)}
          style={{ width: 92 }}
          popupMatchSelectWidth={false}
          optionLabelProp="label"
          filterOption={(input, option) =>
            `${option.value} ${option.country}`.toLowerCase().includes(input.toLowerCase())
          }
          options={CountryCodes.map(({ code: c, country }) => ({
            value: c,
            label: c,
            country,
            // Shown in the dropdown only; the closed select shows just the code.
            children: `${c} ${country}`,
          }))}
          optionRender={(option) => `${option.data.value}  ${option.data.country}`}
        />
      }
    />
  );
};

export default PhoneField;
